import type { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import React, { useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { COLORS } from 'partner360/features/topPartnersManagement/configs'
import { formatMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/getMarketCoverage'
import { hasFlag } from 'partner360/features/topPartnersManagement/helpers/hasFlag'
import { sumMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/sumMarketCoverage'
import { sumPendingAddCoverage } from 'partner360/features/topPartnersManagement/helpers/sumPendingAddCoverage'
import { sumPendingRemoveCoverage } from 'partner360/features/topPartnersManagement/helpers/sumPendingRemoveCoverage'
import { IFlagConfig, IPreparedTopPartner } from 'partner360/features/topPartnersManagement/models'
import { TRANSLATION_KEYS } from 'partner360/features/topPartnersManagement/translations'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'

import styles from './TopPartnersManagementChart.module.scss'

type TChartDimension = 'geoMarket' | 'country'

interface Props {
  data: IPreparedTopPartner[]
  selectedFlags: IP360PartnerFlags[]
  allFlags: IP360PartnerFlags[]
  height?: number
  flagConfigs: IFlagConfig[]
}

const RICH_TEXT_STYLES = {
  inc: { color: '#2dbf6f' },
  dec: { color: '#bf2026' },
}

const getPendingCoverageDelta = (
  pendingAddCoverage: number | null,
  pendingRemoveCoverage: number | null
) => {
  if (pendingAddCoverage === null && pendingRemoveCoverage === null) {
    return null
  }

  const delta = (pendingAddCoverage ?? 0) - (pendingRemoveCoverage ?? 0)

  return delta === 0 ? null : delta
}

const formatCoverageRichLabel = (coverage: number | null, pendingCoverageDelta: number | null) => {
  if (coverage === null && pendingCoverageDelta === null) {
    return ''
  }

  const settledLabel = `Cvg: ${formatMarketCoverage(coverage ?? 0)}`

  if (pendingCoverageDelta === null) {
    return settledLabel
  }

  const styleKey = pendingCoverageDelta > 0 ? 'inc' : 'dec'
  const sign = pendingCoverageDelta > 0 ? '+' : '-'

  return `${settledLabel} {${styleKey}|${sign}${formatMarketCoverage(Math.abs(pendingCoverageDelta))}}`
}

const groupByDimension = (data: IPreparedTopPartner[], dimension: TChartDimension) => {
  const grouped = data.reduce<Record<string, IPreparedTopPartner[]>>((acc, item) => {
    const key = dimension === 'country' ? item.Country : item.GeoMarket

    if (!key) {
      return acc
    }

    acc[key] = acc[key] ? [...acc[key], item] : [item]

    return acc
  }, {})

  const entries = Object.entries(grouped)

  if (dimension === 'geoMarket') {
    entries.sort(
      (a, b) =>
        Math.min(...a[1].map((item) => item.GeoMarketOrder)) -
        Math.min(...b[1].map((item) => item.GeoMarketOrder))
    )
  } else {
    entries.sort((a, b) => b[1].length - a[1].length)
  }

  return entries
}

const prepareData = (
  data: IPreparedTopPartner[],
  flags: IP360PartnerFlags[],
  flagConfigs: IFlagConfig[],
  dimension: TChartDimension
) => {
  const entries = groupByDimension(data, dimension)
  const categories = entries.map(([key]) => key)

  const series = flags.map((flag) => {
    const config = flagConfigs.find((item) => item.bitValue === flag.BitValue)

    return {
      key: flag.BitValue,
      name: flag.Title,
      color: config?.color || '#000',
      coverage: entries.map(([, partners]) => sumMarketCoverage(partners, flag)),
      pendingCoverage: entries.map(([, partners]) =>
        getPendingCoverageDelta(
          sumPendingAddCoverage(partners, flag),
          sumPendingRemoveCoverage(partners, flag)
        )
      ),
      data: entries.map(([, partners]) =>
        partners.reduce((acc, partner) => {
          const isAppliedInBitClass = hasFlag(partner.BitClass, flag.BitValue)

          return isAppliedInBitClass ? acc + 1 : acc
        }, 0)
      ),
    }
  })

  return { categories, series }
}

const EMPTY_CHART_DATA: ReturnType<typeof prepareData> = { categories: [], series: [] }

export const TopPartnersManagementChart = ({
  data,
  selectedFlags,
  flagConfigs,
  height = 200,
  allFlags,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedDimension, setDimension] = useState<TChartDimension | null>(null)
  const defaultDimension = useMemo<TChartDimension>(() => {
    const markets = new Set(data.map((partner) => partner.GeoMarket).filter(Boolean))
    return markets.size === 1 ? 'country' : 'geoMarket'
  }, [data])
  const dimension = selectedDimension ?? defaultDimension

  const collapsedSummary = useMemo(() => {
    const entries = groupByDimension(data, 'geoMarket')
    const flag = selectedFlags[0]
    const total = entries.reduce((sum, [, partners]) => sum + partners.length, 0)

    if (!total) {
      return []
    }

    return entries.map(([key, partners], index) => ({
      key,
      count: partners.length,
      coverage: flag ? sumMarketCoverage(partners, flag) : null,
      pendingCoverage: flag
        ? getPendingCoverageDelta(
            sumPendingAddCoverage(partners, flag),
            sumPendingRemoveCoverage(partners, flag)
          )
        : null,
      widthPercent: (partners.length / total) * 100,
      color: COLORS[index % COLORS.length],
    }))
  }, [data, selectedFlags])

  const preparedData = useMemo(
    () =>
      isExpanded ? prepareData(data, selectedFlags, flagConfigs, dimension) : EMPTY_CHART_DATA,
    [isExpanded, data, selectedFlags, flagConfigs, dimension]
  )

  const preparedLegendData = useMemo(
    () => (isExpanded ? prepareData(data, allFlags, flagConfigs, dimension) : EMPTY_CHART_DATA),
    [isExpanded, data, allFlags, flagConfigs, dimension]
  )

  const orderedVisibleSeries = useMemo(() => {
    return [...preparedData.series].reverse()
  }, [preparedData.series])

  const barOption = useMemo((): EChartsOption => {
    const xAxisValues = preparedData.categories

    const stackSeries = orderedVisibleSeries.map((series) => ({
      name: series.name,
      type: 'bar',
      stack: 'flags',
      barWidth: '50px',
      data: series.data,
      itemStyle: {
        color: series.color,
      },
      label: {
        show: true,
        position: 'top',
        rich: RICH_TEXT_STYLES,
        formatter: (params: any) => {
          if (params.value <= 0) {
            return ''
          }
          const coverage = series.coverage[params.dataIndex]
          const pendingCoverage = series.pendingCoverage[params.dataIndex]
          const coverageLabel = formatCoverageRichLabel(coverage, pendingCoverage)

          return coverageLabel ? `${params.value} (${coverageLabel})` : `${params.value}`
        },
      },
    }))

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        left: 24,
        right: 24,
        top: 20,
        bottom: xAxisValues.length > 12 ? 48 : 12,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisValues,
        axisLabel: {
          interval: 0,
          rotate: xAxisValues.length > 8 ? 30 : 0,
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
      },
      dataZoom: [
        {
          show: xAxisValues.length > 12,
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: Math.min(100, (8 / xAxisValues.length) * 100),
        },
      ],
      series: [...stackSeries] as EChartsOption['series'],
    }
  }, [preparedData.categories, orderedVisibleSeries])

  const legendItems = useMemo(
    () =>
      preparedLegendData.series
        .filter((series) => series.data.some((value) => value > 0))
        .map((series) => ({
          key: series.key,
          displayName: series.name,
          color: series.color,
          count: series.data.reduce((acc, value) => acc + value, 0),
        })),
    [preparedLegendData.series]
  )

  if (!isExpanded) {
    return (
      <div className={styles.card}>
        <div className={styles.collapsedTopRow}>
          <div className={styles.collapsedBar}>
            {collapsedSummary.map((segment) => (
              <span
                key={segment.key}
                style={{ width: `${segment.widthPercent}%`, backgroundColor: segment.color }}
              />
            ))}
          </div>
          <Button
            variant="text"
            iconButton="kbase kbase-chevron-bottom"
            title="Show chart"
            onClick={() => setIsExpanded(true)}
          />
        </div>
        <div className={styles.legend}>
          {collapsedSummary.map((segment) => (
            <span key={segment.key}>
              <i style={{ backgroundColor: segment.color }} />
              {segment.key} <strong>{segment.count}</strong>
              {segment.coverage !== null && (
                <span className={styles.mutedPercent}>
                  {' '}
                  (Cvg: {formatMarketCoverage(segment.coverage)}
                  {segment.pendingCoverage !== null && (
                    <span
                      className={segment.pendingCoverage > 0 ? styles.increase : styles.decrease}
                    >
                      {' '}
                      {segment.pendingCoverage > 0 ? '+' : '-'}
                      {formatMarketCoverage(Math.abs(segment.pendingCoverage))}
                    </span>
                  )}
                  )
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.btns}>
        <Button
          onClick={() => setDimension('geoMarket')}
          variant={dimension === 'geoMarket' ? 'outlined' : 'text'}
        >
          <Translation text={TRANSLATION_KEYS.topPartners.charts.byMarket} />
        </Button>
        <Button
          onClick={() => setDimension('country')}
          variant={dimension === 'country' ? 'outlined' : 'text'}
        >
          <Translation text={TRANSLATION_KEYS.topPartners.charts.byCountry} />
        </Button>
        <Button
          variant="text"
          iconButton="kbase kbase-chevron-top"
          title="Hide chart"
          onClick={() => setIsExpanded(false)}
        />
      </div>
      <ReactECharts option={barOption} style={{ height }} notMerge />
      <div className={styles.legend}>
        {legendItems.map((item) => (
          <span key={item.key}>
            <i style={{ backgroundColor: item.color }} />
            {item.displayName} ({item.count})
          </span>
        ))}
      </div>
    </div>
  )
}
